import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Settings, Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAdminOutputSettings, useUpdateOutputSetting } from "@/services/adminConferenceOutputService";

const SETTING_LABELS: Record<string, { label: string; description: string; suffix?: string }> = {
    payment_valid_hours: { label: "Masa Berlaku Tagihan Pembayaran", description: "Berapa jam tagihan pembayaran biaya publikasi berlaku sejak dibuat.", suffix: "jam" },
    decision_deadline_default_days: { label: "Default Deadline Keputusan Jurnal/Proceeding", description: "Jumlah hari default sejak email dikirim untuk batas keputusan penulis, jika tidak diisi manual saat import.", suffix: "hari" },
    proceeding_submission_deadline_default_days: { label: "Default Deadline Submit Registrasi Proceeding", description: "Jumlah hari default sejak email dikirim untuk batas submit formulir registrasi prosiding.", suffix: "hari" },
};

export default function OutputSettingsPage() {
    const { data: settings, isLoading } = useAdminOutputSettings();
    const updateSetting = useUpdateOutputSetting();
    const [values, setValues] = useState<Record<string, string>>({});
    const [savingKey, setSavingKey] = useState<string | null>(null);

    useEffect(() => {
        if (settings) {
            const map: Record<string, string> = {};
            settings.forEach((s) => { map[s.setting_key] = s.setting_value ?? ""; });
            setValues(map);
        }
    }, [settings]);

    const handleSave = async (key: string) => {
        setSavingKey(key);
        try {
            await updateSetting.mutateAsync({ key, value: values[key] });
            toast.success("Pengaturan berhasil disimpan");
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Gagal menyimpan pengaturan");
        } finally {
            setSavingKey(null);
        }
    };

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 shadow-sm border border-slate-200 dark:border-slate-800 flex items-center gap-5 relative overflow-hidden">
                <div className="absolute -top-12 -right-12 p-12 opacity-5 pointer-events-none"><Settings className="w-64 h-64 text-blue-600" /></div>
                <div className="p-4 rounded-3xl bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400 flex-shrink-0 border border-blue-100 dark:border-blue-800/50 shadow-sm relative z-10"><Settings className="w-8 h-8" /></div>
                <div className="relative z-10">
                    <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">Pengaturan Output Conference</h1>
                    <p className="text-base text-slate-500 dark:text-slate-400 max-w-2xl">Atur durasi deadline dan masa berlaku tagihan secara dinamis tanpa perlu deploy ulang sistem.</p>
                </div>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>
            ) : (
                <div className="space-y-4">
                    {settings?.map((setting) => {
                        const meta = SETTING_LABELS[setting.setting_key] || { label: setting.setting_key, description: "" };
                        return (
                            <div key={setting.id} className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
                                <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8">
                                    <div className="flex-1 min-w-0">
                                        <Label className="text-sm font-bold text-slate-900 dark:text-white">{meta.label}</Label>
                                        {meta.description && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{meta.description}</p>}
                                    </div>
                                    <div className="flex items-center gap-3 shrink-0">
                                        <div className="flex items-center gap-2">
                                            <Input
                                                type="number"
                                                value={values[setting.setting_key] ?? ""}
                                                onChange={(e) => setValues((v) => ({ ...v, [setting.setting_key]: e.target.value }))}
                                                className="w-28 h-11"
                                            />
                                            {meta.suffix && <span className="text-sm text-slate-500 shrink-0">{meta.suffix}</span>}
                                        </div>
                                        <Button
                                            onClick={() => handleSave(setting.setting_key)}
                                            disabled={savingKey === setting.setting_key}
                                            className="rounded-xl h-11 px-6 bg-blue-600 hover:bg-blue-700 shrink-0"
                                        >
                                            {savingKey === setting.setting_key ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                                            Simpan
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
